import { alpha, createTheme } from '@mui/material/styles';

// Central TradePrep theme, in the logo's colours: navy and orange.
// Condensed "site signage" headings, chunky buttons with a hard offset shadow,
// large touch targets and visible focus rings. Light and dark schemes share
// one definition.

const brand = {
  navy: '#0E2352', // logo navy
  navyDark: '#0A1A3F',
  navyLight: '#1B3470',
  orange: '#F47A20', // logo orange
  orangeDark: '#C85A0C',
  orangeBright: '#FF9142', // orange on dark surfaces
  mist: '#F4F6FA', // page background
  ink: '#0B1530', // near-black navy for text, borders and shadows
  muted: '#4A5468', // secondary text on light surfaces
};

/** Hero/cover background used across the app. */
export const brandGradient = `linear-gradient(140deg, ${brand.navyDark} 0%, ${brand.navy} 60%, ${brand.navyLight} 100%)`;

/** Orange/navy hazard tape. */
export const hazardStripes = `repeating-linear-gradient(-45deg, ${brand.orange} 0 14px, ${brand.ink} 14px 28px)`;

/** Faint blueprint grid for dark surfaces. */
export const blueprintGrid =
  'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)';

const darkPaper = '#131C2E';

export const headingFont = '"Barlow Condensed", "Barlow", system-ui, sans-serif';

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'class' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: brand.navy, dark: brand.navyDark, light: brand.navyLight, contrastText: '#ffffff' },
        secondary: { main: brand.orange, dark: brand.orangeDark, contrastText: brand.ink },
        warning: { main: '#b26a00' },
        success: { main: '#1e7d43' },
        error: { main: '#c62828' },
        background: { default: brand.mist, paper: '#ffffff' },
        text: { primary: brand.ink, secondary: brand.muted },
        divider: '#DCE1EA',
      },
    },
    dark: {
      palette: {
        primary: { main: brand.orangeBright, contrastText: brand.ink },
        secondary: { main: brand.orangeBright, contrastText: brand.ink },
        background: { default: '#0B1220', paper: darkPaper },
        divider: '#26324A',
      },
    },
  },
  shape: { borderRadius: 6 },
  typography: {
    fontFamily: '"Barlow", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    h1: { fontFamily: headingFont, fontSize: '2.5rem', fontWeight: 800, letterSpacing: '0.005em', textTransform: 'uppercase', lineHeight: 1.05 },
    h2: { fontFamily: headingFont, fontSize: '2rem', fontWeight: 800, letterSpacing: '0.005em', textTransform: 'uppercase', lineHeight: 1.1 },
    h3: { fontFamily: headingFont, fontSize: '1.625rem', fontWeight: 700 },
    h4: { fontFamily: headingFont, fontSize: '1.5rem', fontWeight: 700 },
    h5: { fontFamily: headingFont, fontSize: '1.3rem', fontWeight: 700 },
    h6: { fontFamily: headingFont, fontSize: '1.125rem', fontWeight: 700, letterSpacing: '0.01em' },
    button: { fontFamily: headingFont, textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', fontSize: '0.95rem', lineHeight: 1.2 },
    overline: { fontFamily: headingFont, fontWeight: 700, letterSpacing: '0.14em' },
    subtitle2: { fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { WebkitFontSmoothing: 'antialiased' },
        // Respect notches / gesture bars on native & installed PWA.
        '#root': { minHeight: '100dvh' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { minHeight: 44, borderRadius: 6, paddingInline: 18 },
        sizeSmall: { minHeight: 36, fontSize: '0.85rem' },
        sizeLarge: { minHeight: 52, fontSize: '1.05rem', paddingInline: 24 },
        // Chunky "stamped" look: solid border + hard offset shadow.
        contained: ({ theme }) => ({
          border: `2px solid ${brand.ink}`,
          boxShadow: `3px 3px 0 ${brand.ink}`,
          transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
          '&:hover': { boxShadow: `4px 4px 0 ${brand.ink}`, transform: 'translate(-1px, -1px)' },
          '&:active': { boxShadow: `1px 1px 0 ${brand.ink}`, transform: 'translate(2px, 2px)' },
          '&.Mui-disabled': { borderColor: 'transparent', boxShadow: 'none' },
          ...theme.applyStyles('dark', {
            borderColor: brand.ink,
            boxShadow: `3px 3px 0 ${alpha('#000', 0.9)}`,
          }),
        }),
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      },
    },
    MuiIconButton: { styleOverrides: { root: { minWidth: 44, minHeight: 44 } } },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
    },
    MuiPaper: { styleOverrides: { rounded: { borderRadius: 8 } } },
    MuiTextField: { defaultProps: { fullWidth: true } },
    MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 4 } } },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `3px solid ${theme.palette.secondary.main}`,
          backgroundColor: alpha(theme.palette.background.paper, 0.94),
          backdropFilter: 'blur(8px)',
          // theme.palette holds the light values; give dark mode its own surface.
          ...theme.applyStyles('dark', {
            borderBottomColor: brand.orangeBright,
            backgroundColor: alpha(darkPaper, 0.94),
          }),
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 6,
          minHeight: 44,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.secondary.main, 0.12),
            color: theme.palette.text.primary,
            fontWeight: 600,
            boxShadow: `inset 4px 0 0 ${theme.palette.secondary.main}`,
            '& .MuiListItemIcon-root': { color: theme.palette.secondary.main },
            ...theme.applyStyles('dark', {
              backgroundColor: alpha(brand.orangeBright, 0.16),
              color: '#ffffff',
              boxShadow: `inset 4px 0 0 ${brand.orangeBright}`,
              '& .MuiListItemIcon-root': { color: brand.orangeBright },
            }),
          },
        }),
      },
    },
    MuiLinearProgress: { styleOverrides: { root: { borderRadius: 3, height: 8 } } },
    MuiButtonBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focusVisible': { outline: `3px solid ${alpha(theme.palette.secondary.main, 0.7)}`, outlineOffset: 2 },
        }),
      },
    },
    MuiTooltip: { defaultProps: { arrow: true } },
    MuiDialog: { defaultProps: { fullWidth: true } },
  },
});

export const brandColors = brand;
